
import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { User } from "../users/user";
import { getUsers } from "../users/routes/getUsers";
import { connectSocket, getSocket } from "./socketManager";

const USERS_DIRECTORY_STORAGE_KEY = "usersDirectory";

export interface UserDirectoryItem {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
}

const buildUserDirectory = (users: User[]): UserDirectoryItem[] => {
  return users
    .filter((user) => user.id != null)
    .map((user) => {
      const firstName = user.firstName || "";
      const lastName = user.lastName || "";
      const displayName = `${firstName} ${lastName}`.trim() || user.email || `Utente ${user.id}`;

      return {
        id: String(user.id),
        firstName,
        lastName,
        displayName,
      };
    });
};

const readStoredUserDirectory = (): UserDirectoryItem[] => {
  try {
    const storedValue = localStorage.getItem(USERS_DIRECTORY_STORAGE_KEY);
    return storedValue ? JSON.parse(storedValue) as UserDirectoryItem[] : [];
  } catch (error) {
    console.error("Errore lettura cache utenti", error);
    return [];
  }
};

export const getCachedUserDisplayName = (userId: string | number | null | undefined): string => {
  if (userId == null) return "";
  const directory = readStoredUserDirectory();
  return directory.find((user) => user.id === String(userId))?.displayName || "";
};

interface AuthContextType {
  isAuthenticated: boolean;
  userRole: string | null;
  token: string | null;
  userName: string;
  userId: string | null;
  login: (token: string, role: string, name: string, id: string) => void;
  logout: () => void;
  users: User[];
  userDirectory: UserDirectoryItem[];
  getUserDisplayName: (userId: string | number | null | undefined) => string;
  refreshUsers: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/*
ADMIN: ACCESSO COMPLETO
SUPER USER: ACCESSO COMPLETO SULLA GESTIONE DEI PROGETTI, NESSUN ACCESSO SULLA GESTIONE DEGLI UTENTI
USER: ACCESSO LIMITATO AI PROGETTI NEI QUALI E' INCLUSO
EXTERNAL: ACCESSO LIMITATO ALLE FASI DI PROGETTO NELLE QUALI E' INCLUSO, SOLA LETTURA AD ESCLUSIONE DI NOTE E SEGNALAZIONI
*/

export const AuthProvider = ({ children }: { children: ReactNode }) => {

  const [users, setUsers] = useState<User[]>([]);
  const [userDirectory, setUserDirectory] = useState<UserDirectoryItem[]>(readStoredUserDirectory);
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(localStorage.getItem("authToken"));
  const [role, setRole] = useState<string | null>(localStorage.getItem("userRole"));
  const [name, setName] = useState<string | null>(localStorage.getItem("userName"));
  const [userId, setId] = useState<string | null>(localStorage.getItem("userId"));
  // Stato interno per gestire il caricamento iniziale della sessione
  const [isInitializing, setIsInitializing] = useState(true);

  const login = (newToken: string, role: string, name: string, id: string) => {

    localStorage.setItem("authToken", newToken);
    localStorage.setItem("userRole", role.toString());
    localStorage.setItem("userName", name);
    localStorage.setItem("userId", id.toString());

    setToken(newToken);
    setRole(role.toString());
    setName(name);
    setId(id);
    setIsAuth(true);

    // 🚀 Connessione immediata dopo il login riuscito
    connectSocket();

    refreshUsers();
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem("authToken");
    setRole(null);
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    localStorage.removeItem("userId");
    localStorage.removeItem(USERS_DIRECTORY_STORAGE_KEY);
    setUsers([]);
    setUserDirectory([]);
    setIsAuth(false);
    // Opzionale: disconnetti il socket al logout se vuoi pulizia totale
    getSocket()?.disconnect();
  };

  const refreshUsers = useCallback(async () => {
    try {
      const res = await getUsers();
      if (res.ok) {
        const data = await res.json() as User[];
        const directory = buildUserDirectory(data);

        setUsers(data);
        setUserDirectory(directory);
        localStorage.setItem(USERS_DIRECTORY_STORAGE_KEY, JSON.stringify(directory));
      }
    } catch (e) {
      console.error("Errore nel recupero utenti", e);
    }
  }, []);

  const getUserDisplayName = useCallback((lookupUserId: string | number | null | undefined) => {
    if (lookupUserId == null) return "";
    return userDirectory.find((user) => user.id === String(lookupUserId))?.displayName || "";
  }, [userDirectory]);

  /**
     * 🛡️ LOGICA DI INIZIALIZZAZIONE SESSIONE
     * Verifica se il token esistente è ancora valido tramite refresh
     */
  /*  useEffect(() => {
      const token = localStorage.getItem("authToken");
      if (token) {
        connectSocket();
      }
    }, []);*/
  /*
    useEffect(() => {
      const initAuth = async () => {
        const storedToken = localStorage.getItem("authToken");
  
        if (storedToken) {
          // 1. Settiamo subito il token per sbloccare le rotte protette
          setToken(storedToken);
  
          // 2. Sblocchiamo l'interfaccia IMMEDIATAMENTE
          // In questo modo i componenti vengono montati e i listener socket sono pronti
          setIsInitializing(false);
  
          try {
            const newToken = await refreshToken();
            if (newToken) {
              setToken(newToken);
              console.log("✅ Token aggiornato, connetto...");
              connectSocket();
            } else {
              console.warn("⚠️ Refresh fallito, provo con il vecchio o esco...");
              // Se il refresh fallisce ma avevi un token, proviamo a connettere comunque
              // Se il server lo rifiuta, scatterà il connect_error che abbiamo gestito
              connectSocket();
            }
          } catch (err) {
            console.error("🌐 Errore rete, tento comunque connessione socket");
            connectSocket();
          }
        } else {
          // Nessun token, sblocchiamo comunque per andare al login
          setIsInitializing(false);
        }
      };
  
      initAuth();
    }, []); // Rimuovi logout dalle dipendenze per evitare trigger ciclici
  */

  useEffect(() => {
    const initAuth = () => {
      const storedToken = localStorage.getItem("authToken");

      if (storedToken) {
        // 1. Carichiamo il token esistente
        setToken(storedToken);

        // 2. Connettiamo il socket direttamente
        // Se il token è scaduto, il socket gestirà l'errore internamente
        connectSocket();
        refreshUsers();
      }

      // 3. Sblocchiamo l'interfaccia
      setIsInitializing(false);
    };

    initAuth();
  }, [refreshUsers]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        userRole: role,
        token,
        login,
        logout,
        userName: name || "",
        userId: userId || null,
        users,
        userDirectory,
        getUserDisplayName,
        refreshUsers
      }}
    >
      {/* Evitiamo di renderizzare l'app finché non abbiamo verificato la sessione  
      {children}
      </AuthContext.Provider> */}
      {!isInitializing && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};


